import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import axios from 'axios'
import { PageTitle } from '../../../../_metronic/layout/core'
import { PublicDeadlineProgressCard } from './components/PublicDeadlineProgressCard'
import { PublicKanbanBoard } from './components/PublicKanbanBoard'
import { PublicCycleView } from './components/PublicCycleView'
import { PublicSchedule } from './components/PublicSchedule'
import { formatDateToBrazilian } from '../../../../_metronic/helpers/AssetHelpers'

type ProjectData = {
  id: number
  name: string
  client_name: string
  begin_date: string
  end_date: string
  expected_date?: string
  status: string
  created_at: string
  closed_at?: string | null
  type: number // 0 = recorrente, 1 = escopo fechado
  cycle_info?: {
    budgeted_hours: number
    used_time: number
    cycle: number
    cycles_filter: Array<{
      id: number
      begin_date: string
      end_date: string
      cycle: number
    }>
  }
  show_deadline?: boolean
  show_cycle_info?: boolean
  show_schedule?: boolean
  show_tasks?: boolean
}

const ClientAreaPage: FC = () => {
  const intl = useIntl()
  const { clientUrl } = useParams()
  const [project, setProject] = useState<ProjectData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true)
        const apiUrl = process.env.REACT_APP_API_URL; 
        
        const response = await axios.get(
          `${apiUrl}/api/public/client/${clientUrl}`
        )
        setProject(response.data.data)
      } catch (error: any) {
        console.error('Erro ao carregar projeto:', error)
        setError(error.response?.data?.message || 'Erro ao carregar projeto')
      } finally {
        setLoading(false)
      }
    }

    if (clientUrl) {
      fetchProject()
    }
  }, [clientUrl])

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Não informado'
    return formatDateToBrazilian(dateString)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Em andamento':
        return 'primary'
      case 'Concluído':
        return 'success'
      case 'Atrasado':
        return 'danger'
      case 'Não iniciado':
        return 'warning'
      default:
        return 'secondary'
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="alert alert-danger text-center">
              <i className="bi bi-exclamation-triangle fs-1 mb-3"></i>
              <h4>Erro ao carregar projeto</h4>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="alert alert-warning text-center">
              <i className="bi bi-info-circle fs-1 mb-3"></i>
              <h4>Projeto não encontrado</h4>
              <p>O link fornecido não é válido ou o projeto não existe.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageTitle>Portal do Cliente - {project.name}</PageTitle>
      
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-12 col-lg-11 col-xl-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mb-4">
                <i className="bi bi-building fs-1 text-primary"></i>
              </div>
              <h1 className="fs-2hx fw-bold text-gray-900 mb-3">
                {project.name}
              </h1>
              <p className="fs-4 text-muted">
                Área de acompanhamento do projeto
              </p>
            </div>

            {/* Status Card */}
            <div className="card mb-6">
              <div className="card-body text-center">
                <div className="mb-3">
                  <span className={`badge badge-${getStatusColor(project.status)} fs-6 px-4 py-3`}>
                    <i className="bi bi-circle-fill me-2"></i>
                    {project.status}
                  </span>
                </div>
                <p className="text-muted mb-0">
                  Status atual do projeto
                </p>
              </div>
            </div>

            {/* Project Info */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Informações do Projeto
                </h3>
              </div>
              <div className="card-body">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex flex-column">
                      <span className="fw-bold text-gray-700 mb-2">
                        <i className="bi bi-building me-2"></i>
                        Cliente
                      </span>
                      <span className="text-gray-900 fs-6">
                        {project.client_name}
                      </span>
                    </div>
                  </div>
                  
                  {/* Data de Início e Data Prevista de Conclusão lado a lado */}
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between gap-3">
                      <div className="d-flex flex-column flex-fill">
                        <span className="fw-bold text-gray-700 mb-2">
                          <i className="bi bi-calendar-event me-2"></i>
                          Data de Início
                        </span>
                        <span className="text-gray-900 fs-6">
                          {formatDate(project.begin_date)}
                        </span>
                      </div>
                      
                      <div className="d-flex flex-column flex-fill">
                        <span className="fw-bold text-gray-700 mb-2">
                          <i className="bi bi-calendar-check me-2"></i>
                          Data prevista de conclusão
                        </span>
                        <span className="text-gray-900 fs-6">
                          {formatDate(project.end_date)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Criado em e Fechado em (se existir) lado a lado */}
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between gap-3">
                      <div className="d-flex flex-column flex-fill">
                        <span className="fw-bold text-gray-700 mb-2">
                          <i className="bi bi-clock me-2"></i>
                          Criado em
                        </span>
                        <span className="text-gray-900 fs-6">
                          {formatDate(project.created_at)}
                        </span>
                      </div>
                      
                      {project.closed_at ? (
                        <div className="d-flex flex-column flex-fill">
                          <span className="fw-bold text-gray-700 mb-2">
                            <i className="bi bi-calendar-x me-2"></i>
                            Data de Fechamento
                          </span>
                          <span className="text-gray-900 fs-6">
                            {formatDate(project.closed_at)}
                          </span>
                        </div>
                      ) : (
                        <div className="d-flex flex-column flex-fill"></div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Prazo do Projeto - Componente Completo */}
                {(project.show_deadline != false) && (
                  <div className="row g-4 mt-2">
                    <div className="col-12">
                      <div className="separator separator-dashed my-4"></div>
                      <PublicDeadlineProgressCard 
                        beginDate={project.begin_date}
                        endDate={project.end_date}
                        expectedDate={project.expected_date}
                        status={project.status}
                      />
                    </div>
                  </div>
                )}

                {/* Informações do Ciclo - Apenas para projetos recorrentes */}
                {(project.show_cycle_info != false) && project.type === 0 && project.cycle_info && (
                  <div className="row g-4 mt-2">
                    <div className="col-12">
                      <div className="separator separator-dashed my-4"></div>
                      <PublicCycleView clientUrl={clientUrl!} />
                    </div>
                  </div>
                )}
                
                {/* Cronograma do Projeto */}
                {(project.show_schedule != false) && (
                  <div className="row g-4 mt-2">
                    <div className="col-12">
                      <PublicSchedule clientUrl={clientUrl!} />
                    </div>
                  </div>
                )}

                {/* Tarefas do Projeto - Kanban */}
                {(project.show_tasks != false) && (
                  <div className="row g-4 mt-2">
                    <div className="col-12">
                      <div className="separator separator-dashed my-4"></div>
                      <PublicKanbanBoard clientUrl={clientUrl!} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-muted fs-7">
                <i className="bi bi-shield-check me-2"></i>
                Esta é uma área segura para acompanhamento do seu projeto
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { ClientAreaPage }
